package com.evoapps.evoshift.domain.repository

import com.evoapps.evoshift.domain.model.FinancialEngagement
import com.evoapps.evoshift.domain.model.StaffReadiness
import com.evoapps.evoshift.domain.model.StaffRecord
import com.evoapps.evoshift.domain.timeline.EngagementDates
import com.evoapps.evoshift.domain.timeline.TimelineAuditEntry
import com.evoapps.evoshift.domain.timeline.TimelineNote

/**
 * SH-212: the employee timeline's own documents, and the one batch that writes an edit back to
 * the data it is a view of.
 * - users/{uid}/timeline/{wpId}: the free notes and the day after a planned end of cooperation
 *   (functions/timeline.js ends the engagement then). The person reads it, the workplace's owner
 *   and financial managers read and write it (firestore.rules).
 * - users/{uid}/timeline_audit/{wpId}: who changed what, when. Owners and financial managers
 *   only: it names colleagues, so not the person.
 */
interface EmployeeTimelineRepository {
    /** [userId]'s timeline document at [workplaceId] (empty: none). The person and the workplace's owner / financial managers. */
    suspend fun getTimelineDoc(userId: String, workplaceId: String): Result<TimelineDoc>

    /** The audit trail, newest last (empty: none). Owners and financial managers only. */
    suspend fun getAudit(userId: String, workplaceId: String): Result<List<TimelineAuditEntry>>

    /** Writes [write] in one batch: firestore.rules check each part as the batch leaves the profile. */
    suspend fun save(write: TimelineWriteBatch): Result<Unit>

    /**
     * SH-269: several people's edits at one workplace in one batch (a bulk change: all of them or
     * none). Without a batch of its own (test fakes) one after another.
     */
    suspend fun saveAll(writes: List<TimelineWriteBatch>): Result<Unit> = runCatching {
        writes.forEach { save(it).getOrThrow() }
    }
}

/**
 * The parts of one timeline edit; null parts stay as they are. [audit] is the whole trail after
 * the edit (at most TimelineAudit.MAX_ENTRIES).
 */
data class TimelineWriteBatch(
    val userId: String,
    val workplaceId: String,
    val editorId: String,
    val engagement: EngagementDates? = null,
    val financial: FinancialEngagement? = null,
    val staff: StaffRecord? = null,
    val readiness: StaffReadiness? = null,
    /** The notes and planned end, written together when either changed. */
    val timelineDoc: TimelineDoc? = null,
    val audit: List<TimelineAuditEntry>,
    /** SH-232: the workplace's periods without pay (WorkplaceRepository.updateUnpaidWork's fields). */
    val unpaid: UnpaidWorkWrite? = null,
    /** SH-232: the person's labour inclusion setting (workplaces/{wpId}/labour_inclusion/{uid}; empty: it goes). */
    val labourInclusion: List<com.evoapps.evoshift.domain.budget.LabourInclusionPeriod>? = null
)

/**
 * SH-232: everyone's periods without pay at the workplace (settings.unpaidPeriods), the older list
 * of the people unpaid on [asOf] (settings.payroll.unpaidUserIds) and that day (settings.unpaidPeriodsAsOf).
 */
data class UnpaidWorkWrite(
    val periods: Map<String, List<com.evoapps.evoshift.domain.budget.UnpaidPeriod>>,
    val unpaidUserIds: List<String>,
    val asOf: String
)

/** users/{uid}/timeline/{wpId}: the notes, and the day after a planned end of cooperation ("YYYY-MM-DD"). */
data class TimelineDoc(val notes: List<TimelineNote> = emptyList(), val plannedEndOn: String? = null)
